import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import React, { useState } from 'react';
import { InputDialog } from 'wix-ui-tpa/cssVars';
import {
  CartFlow,
  CartModalStatus,
  ReservedPaymentOptionIds,
} from '../../../../types/types';
import { useFormActions } from '../../Hooks/useFormActions';
import { CartModalDataHooks } from './dataHooks';
import { ModalContent } from './ModalContent/ModalContent';
import { ButtonProp, ModalFooter } from './ModalFooter/ModalFooter';
import { classes } from './CartModal.st.css';
import { MobileModal } from './MobileModal/MobileModal';
import { FormSelectedSlot } from '../../../../types/formSelectedSlots';
import { ServiceData, SlotService } from '../../../../utils/state/types';
import { mapToArray } from '../../../../utils';

export interface CartModalProps {
  cartModalStatus: CartModalStatus;
  serviceData: ServiceData;
  formSelectedSlot: FormSelectedSlot;
  dateRegionalSettingsLocale: string;
  cartLineItems: any[];
}

const CartModal: React.FC<CartModalProps> = ({
  cartModalStatus,
  serviceData,
  dateRegionalSettingsLocale,
  formSelectedSlot,
  cartLineItems,
}) => {
  const { t } = useTranslation();
  const { onCartCTAClick } = useFormActions();
  const { isMobile } = useEnvironment();
  const isSingleItemOnCart = cartLineItems?.length === 1;
  let shouldSecondaryButtonBeCheckout = false;

  const slotService = mapToArray<SlotService>(serviceData.slotServices)[0];
  const { service, selectedPaymentOption, memberships } = slotService;

  if (isSingleItemOnCart) {
    const isFreePrice =
      service?.payment?.paymentDetails?.isFree &&
      selectedPaymentOption?.id === ReservedPaymentOptionIds.SingleSession;

    const isCustomPrice =
      service?.payment?.paymentDetails?.priceText &&
      selectedPaymentOption?.id === ReservedPaymentOptionIds.SingleSession;

    const isMembershipOptionSelected = !!memberships?.eligibleMemberships?.some(
      (membership) => selectedPaymentOption?.id === membership.id,
    );

    shouldSecondaryButtonBeCheckout =
      isSingleItemOnCart &&
      (isFreePrice ||
        isCustomPrice ||
        isMembershipOptionSelected ||
        service.isPendingApprovalFlow);
  }

  const isOpen = cartModalStatus === CartModalStatus.OPEN;
  const onClose = () => onCartCTAClick(slotService, CartFlow.ADD_MORE_SESSIONS);

  const title = t('app.booking-details.cart-modal.title');

  const [primaryButtonLoading, setPrimaryButtonLoading] =
    useState<boolean>(false);
  const [secondaryaryButtonLoading, setSecondaryaryButtonLoading] =
    useState<boolean>(false);

  const primaryButton: ButtonProp = {
    dataHook: CartModalDataHooks.PRIMARY_BUTTON,
    text: t('app.booking-details.cart-modal.cta.add-more-sessions.label'),
    onClick: async () => {
      setPrimaryButtonLoading(true);
      await onCartCTAClick(slotService, CartFlow.ADD_MORE_SESSIONS);
    },
    loading: primaryButtonLoading,
  };
  const secondaryButton: ButtonProp = {
    dataHook: CartModalDataHooks.SECONDARY_BUTTON,
    text: t(
      shouldSecondaryButtonBeCheckout
        ? 'app.booking-details.cart-modal.cta.complete-checkout.label'
        : 'app.booking-details.cart-modal.cta.checkout.label',
    ),
    onClick: async () => {
      setSecondaryaryButtonLoading(true);
      await onCartCTAClick(slotService, CartFlow.CHECKOUT);
    },
    loading: secondaryaryButtonLoading,
  };

  if (isMobile) {
    return (
      <MobileModal
        primaryButton={primaryButton}
        secondaryButton={secondaryButton}
        serviceData={serviceData}
        formSelectedSlot={formSelectedSlot}
        dateRegionalSettingsLocale={dateRegionalSettingsLocale}
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        dataHook={CartModalDataHooks.MOBILE_MODAL}
      />
    );
  }

  return (
    <InputDialog
      className={classes.root}
      data-hook={CartModalDataHooks.INPUT_DIALOG}
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      customFooter={
        <ModalFooter
          primaryButton={primaryButton}
          secondaryButton={secondaryButton}
        />
      }
    >
      <div className={classes.contentWrapper}>
        <ModalContent
          serviceData={serviceData}
          formSelectedSlot={formSelectedSlot}
          dateRegionalSettingsLocale={dateRegionalSettingsLocale}
        />
      </div>
    </InputDialog>
  );
};

export default CartModal;
